import React, { useState, useEffect } from 'react';
import { FiLock, FiClock } from 'react-icons/fi';
import { useSelector } from 'react-redux';
import { getUserName } from '../../../../Redux/selecters';
import PasscodePopup from './PasscodePopup';
import { useScreenLock } from './ScreenLockContext';

const ScreenLockOverlay = () => {
  const { isLocked, lockTime, unlockScreen } = useScreenLock();
  const [showPasscodePopup, setShowPasscodePopup] = useState(false);
  const userName = useSelector(getUserName);

  // Prevent scrolling when locked
  useEffect(() => {
    if (isLocked) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isLocked]);

  const handleUnlockClick = () => {
    setShowPasscodePopup(true);
  };

  const handlePasscodeSubmit = (success) => {
    setShowPasscodePopup(false);
    if (success) {
      unlockScreen();
    }
  };

  const formatLockTime = (time) => {
    if (!time) return '';
    return new Intl.DateTimeFormat('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    }).format(time);
  };

  // Prevent context menu and other interactions
  const handleContextMenu = (e) => {
    e.preventDefault();
  };

  const handleKeyDown = (e) => {
    // Allow only escape key to close (optional)
    if (e.key !== 'Escape') {
      e.preventDefault();
    }
  };

  if (!isLocked) return null;

  return (
    <div style={styles.overlay} className="screen-lock-overlay" onContextMenu={handleContextMenu} onKeyDown={handleKeyDown} tabIndex={-1}>
      <div style={styles.container} className="screen-lock-container">
        <div style={styles.lockIcon} className="lock-icon">
          <FiLock size={80} />
        </div>

        <h2 style={styles.title}>System Locked</h2>

        <div style={styles.userInfo}>
          <p style={styles.userName}>{userName || 'User'}</p>
          {lockTime && (
            <div style={styles.timeInfo}>
              <FiClock size={16} />
              <span>Locked at {formatLockTime(lockTime)}</span>
            </div>
          )}
        </div>

        <button style={styles.unlockButton} className="unlock-button" onClick={handleUnlockClick} type="button">
          <FiLock size={20} />
          Unlock System
        </button>

        <p style={styles.instruction}>Enter your 4-digit passcode to unlock the system</p>
      </div>

      {showPasscodePopup && <PasscodePopup onSubmit={handlePasscodeSubmit} />}
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.95)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 9999,
    backdropFilter: 'blur(10px)',
    userSelect: 'none',
  },
  container: {
    textAlign: 'center',
    color: 'white',
    maxWidth: '400px',
    padding: '40px',
  },
  lockIcon: {
    marginBottom: '30px',
    opacity: 0.8,
  },
  title: {
    fontSize: '28px',
    fontWeight: 'bold',
    marginBottom: '20px',
    color: 'white',
  },
  userInfo: {
    marginBottom: '30px',
  },
  userName: {
    fontSize: '18px',
    marginBottom: '10px',
    color: '#ccc',
  },
  timeInfo: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    fontSize: '14px',
    color: '#999',
  },
  unlockButton: {
    backgroundColor: '#007bff',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '12px 24px',
    fontSize: '16px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    margin: '0 auto 20px auto',
    transition: 'background-color 0.3s ease',
    outline: 'none',
  },
  instruction: {
    fontSize: '14px',
    color: '#ccc',
    marginTop: '20px',
  },
};

export default ScreenLockOverlay;
